/* eslint-disable no-console */
/**
 * Modal de clasificación POR PROVEEDOR (Fase A — SII recibidas).
 *
 * Se abre desde una factura, pero la decisión es a nivel PROVEEDOR: define la naturaleza del
 * proveedor (Gasto / Activo / Híbrido) y, si es propagable, clasifica de una vez todas sus
 * recibidas 2026 sin clasificar. La lógica vive en `utils/compras/clasificacionProveedor.js`.
 *
 * Requiere `proveedorId`. Las recibidas sin proveedor identificado siguen yendo a
 * `AsignarRecibidaModal` (que primero resuelve el proveedor) — ver ComprasTab.
 *
 * Híbrido (caso Blackim): el proveedor mezcla gasto y activo, así que NO se fija regla
 * propagable; se marca el modo (para que sus futuras facturas no se auto-asignen) y se
 * clasifica SOLO la factura abierta, eligiendo su naturaleza.
 */
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { CUENTAS_ER } from "../../constants/clasificacionContable";
import {
  MODO,
  aplicarReglaProveedor,
  clasificarFacturaUnica,
  desvincularReglaProveedor,
  previewDesvinculoRegla,
} from "../../utils/compras/clasificacionProveedor";
import { getProveedorPorId } from "../../utils/proveedores";

const CUENTAS_POR_GRUPO = [...new Set(CUENTAS_ER.map((c) => c.grupo))].map((grupo) => ({
  grupo,
  cuentas: CUENTAS_ER.filter((c) => c.grupo === grupo),
}));
const labelCuenta = (v) => CUENTAS_ER.find((c) => c.value === v)?.label || v;

const selectStyle = {
  width: "100%",
  height: 36,
  fontSize: "0.82rem",
  padding: "4px 10px",
  border: "1px solid #ced4da",
  borderRadius: 4,
};

const SelectorCuenta = ({ value, onChange }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} style={selectStyle}>
    <option value="">— Seleccionar cuenta —</option>
    {CUENTAS_POR_GRUPO.map(({ grupo, cuentas }) => (
      <optgroup key={grupo} label={grupo}>
        {cuentas.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </optgroup>
    ))}
  </select>
);

const Radio = ({ name, value, checked, onChange, children }) => (
  <label style={{ cursor: "pointer" }}>
    <input type="radio" name={name} value={value} checked={checked} onChange={onChange} style={{ marginRight: 4 }} />
    {children}
  </label>
);

const ClasificarProveedorModal = ({ doc, onClose, onAplicada }) => {
  const { proveedorId, proveedorNombre, pais, id: facturaId } = doc;

  const [prov, setProv] = useState(null);
  const [modo, setModo] = useState(MODO.GASTO);
  const [cuentaER, setCuentaER] = useState("");
  // Sub-elección del modo híbrido: cómo se clasifica ESTA factura (no propaga).
  const [hibTipo, setHibTipo] = useState(MODO.GASTO);
  const [hibCuenta, setHibCuenta] = useState("");
  const [guardando, setGuardando] = useState(false);
  // Diálogo de confirmación del desvínculo (acción en masa): { revertidas, manuales } o null.
  const [desvinculoPreview, setDesvinculoPreview] = useState(null);

  useEffect(() => {
    let cancelado = false;
    getProveedorPorId(proveedorId)
      .then((p) => {
        if (cancelado || !p) return;
        setProv(p);
        // Prefill: si ya hay regla, se refleja; si no, el cuenta_er_default viejo sirve de
        // sugerencia (no es regla hasta guardar por acá).
        if (p.clasificacion_modo) setModo(p.clasificacion_modo);
        if (p.cuenta_er_default) {
          setCuentaER(p.cuenta_er_default);
          setHibCuenta(p.cuenta_er_default);
        }
      })
      .catch((err) => console.error("ClasificarProveedorModal: error cargando proveedor", err));
    return () => {
      cancelado = true;
    };
  }, [proveedorId]);

  const reglaActual = prov?.clasificacion_modo;

  const guardar = async () => {
    if (modo === MODO.GASTO && !cuentaER) return toast.error("Elegí una cuenta del ER");
    if (modo === MODO.HIBRIDO && hibTipo === MODO.GASTO && !hibCuenta)
      return toast.error("Elegí la cuenta para esta factura");

    setGuardando(true);
    try {
      if (modo === MODO.HIBRIDO) {
        // Marca el proveedor híbrido (no propaga) y clasifica solo la factura abierta.
        await aplicarReglaProveedor({ proveedorId, pais, modo: MODO.HIBRIDO });
        await clasificarFacturaUnica({ facturaId, modo: hibTipo, cuentaEr: hibTipo === MODO.GASTO ? hibCuenta : null });
        toast.success("Proveedor marcado como híbrido · esta factura clasificada");
      } else {
        const { propagadas } = await aplicarReglaProveedor({ proveedorId, pais, modo, cuentaEr: cuentaER });
        toast.success(
          propagadas > 0
            ? `Regla aplicada · ${propagadas} factura${propagadas === 1 ? "" : "s"} clasificada${propagadas === 1 ? "" : "s"}`
            : "Regla guardada (no había facturas sin clasificar)"
        );
      }
      onAplicada?.();
    } catch (err) {
      console.error("ClasificarProveedorModal: error al guardar", err);
      toast.error("No se pudo aplicar la clasificación");
    } finally {
      setGuardando(false);
    }
  };

  // Paso 1: calcular los conteos reales y mostrar el diálogo de confirmación (nada silencioso).
  const pedirDesvincular = async () => {
    setGuardando(true);
    try {
      const preview = await previewDesvinculoRegla({ proveedorId, pais });
      setDesvinculoPreview(preview);
    } catch (err) {
      console.error("ClasificarProveedorModal: error al calcular el desvínculo", err);
      toast.error("No se pudo calcular el desvínculo");
    } finally {
      setGuardando(false);
    }
  };

  // Paso 2: ejecutar. Limpia la regla y revierte las propagadas (manual=false); conserva las
  // excepciones manuales.
  const confirmarDesvincular = async () => {
    setGuardando(true);
    try {
      const { revertidas, manuales } = await desvincularReglaProveedor({ proveedorId, pais });
      toast.success(
        `Regla eliminada · ${revertidas} factura${revertidas === 1 ? "" : "s"} a sin clasificar` +
          (manuales > 0
            ? ` · ${manuales} excepción${manuales === 1 ? "" : "es"} conservada${manuales === 1 ? "" : "s"}`
            : "")
      );
      onAplicada?.();
    } catch (err) {
      console.error("ClasificarProveedorModal: error al desvincular", err);
      toast.error("No se pudo eliminar la regla");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen toggle={onClose} size="md">
      <ModalHeader toggle={onClose}>Clasificar proveedor — {proveedorNombre}</ModalHeader>
      {desvinculoPreview ? (
        <>
          <ModalBody>
            <div style={{ fontSize: "0.85rem", lineHeight: 1.6 }}>
              Vas a desvincular la regla de <strong>{proveedorNombre}</strong>. Se devolverán{" "}
              <strong>{desvinculoPreview.revertidas}</strong> factura{desvinculoPreview.revertidas === 1 ? "" : "s"} a{" "}
              <strong>sin clasificar</strong>.
              {desvinculoPreview.manuales > 0 && (
                <>
                  {" "}
                  Se conservan <strong>{desvinculoPreview.manuales}</strong> excepción
                  {desvinculoPreview.manuales === 1 ? "" : "es"} manual
                  {desvinculoPreview.manuales === 1 ? "" : "es"}.
                </>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              outline
              size="sm"
              onClick={() => setDesvinculoPreview(null)}
              disabled={guardando}
              style={{ marginRight: 8 }}
            >
              Cancelar
            </Button>
            <Button
              color="danger"
              size="sm"
              style={{ minWidth: 110 }}
              onClick={confirmarDesvincular}
              disabled={guardando}
            >
              {guardando ? "Desvinculando…" : "Confirmar"}
            </Button>
          </ModalFooter>
        </>
      ) : (
        <>
          <ModalBody>
            {reglaActual && (
              <div
                style={{
                  background: "#eafaf1",
                  border: "1px solid #abebc6",
                  color: "#1e8449",
                  padding: "8px 12px",
                  borderRadius: 6,
                  fontSize: "0.78rem",
                  marginBottom: 14,
                }}
              >
                Regla actual:{" "}
                <strong>
                  {reglaActual === MODO.GASTO && `Gasto → ${labelCuenta(prov.cuenta_er_default)}`}
                  {reglaActual === MODO.ACTIVO && "Activo"}
                  {reglaActual === MODO.HIBRIDO && "Híbrido (se clasifica factura por factura)"}
                </strong>
                . Cambiala abajo (re-propaga a las que sigan sin clasificar) o desvinculala.
              </div>
            )}

            <div style={{ fontSize: "0.72rem", color: "#909497", marginBottom: 12 }}>
              La regla se aplica a TODAS las facturas SII recibidas 2026 de este proveedor que estén sin clasificar.
              Nunca pisa una clasificación previa.
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                Naturaleza del proveedor
              </label>
              <div style={{ display: "flex", gap: 16, fontSize: "0.82rem", flexWrap: "wrap" }}>
                <Radio
                  name="modoProv"
                  value={MODO.GASTO}
                  checked={modo === MODO.GASTO}
                  onChange={() => setModo(MODO.GASTO)}
                >
                  Gasto
                </Radio>
                <Radio
                  name="modoProv"
                  value={MODO.ACTIVO}
                  checked={modo === MODO.ACTIVO}
                  onChange={() => setModo(MODO.ACTIVO)}
                >
                  Activo
                </Radio>
                <Radio
                  name="modoProv"
                  value={MODO.HIBRIDO}
                  checked={modo === MODO.HIBRIDO}
                  onChange={() => setModo(MODO.HIBRIDO)}
                >
                  Híbrido (mezcla)
                </Radio>
              </div>
            </div>

            {modo === MODO.GASTO && (
              <div style={{ marginBottom: 4 }}>
                <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Cuenta del Estado de Resultados
                </label>
                <SelectorCuenta value={cuentaER} onChange={setCuentaER} />
              </div>
            )}

            {modo === MODO.ACTIVO && (
              <div
                style={{
                  background: "#eaf2f8",
                  color: "#2874a6",
                  padding: "8px 12px",
                  borderRadius: 6,
                  fontSize: "0.78rem",
                }}
              >
                Sus recibidas 2026 sin clasificar se marcan <strong>activo</strong> y salen de la línea de costo del ER.
                El alta / depreciación del activo se hace aparte.
              </div>
            )}

            {modo === MODO.HIBRIDO && (
              <div style={{ border: "1px dashed #ccc", borderRadius: 6, padding: "10px 12px" }}>
                <div style={{ fontSize: "0.72rem", color: "#909497", marginBottom: 8 }}>
                  El proveedor mezcla gasto y activo: no se propaga. Se clasifica <strong>solo esta factura</strong> y
                  sus futuras facturas no se auto-asignan.
                </div>
                <div style={{ display: "flex", gap: 16, fontSize: "0.82rem", marginBottom: 8 }}>
                  <Radio
                    name="hibTipo"
                    value={MODO.GASTO}
                    checked={hibTipo === MODO.GASTO}
                    onChange={() => setHibTipo(MODO.GASTO)}
                  >
                    Esta = Gasto
                  </Radio>
                  <Radio
                    name="hibTipo"
                    value={MODO.ACTIVO}
                    checked={hibTipo === MODO.ACTIVO}
                    onChange={() => setHibTipo(MODO.ACTIVO)}
                  >
                    Esta = Activo
                  </Radio>
                </div>
                {hibTipo === MODO.GASTO && <SelectorCuenta value={hibCuenta} onChange={setHibCuenta} />}
              </div>
            )}
          </ModalBody>
          <ModalFooter style={{ justifyContent: "space-between" }}>
            <span>
              {reglaActual && (
                <Button
                  color="link"
                  size="sm"
                  style={{ color: "#c0392b", padding: 0 }}
                  onClick={pedirDesvincular}
                  disabled={guardando}
                >
                  Desvincular regla
                </Button>
              )}
            </span>
            <span>
              <Button
                color="secondary"
                outline
                size="sm"
                onClick={onClose}
                disabled={guardando}
                style={{ marginRight: 8 }}
              >
                Cancelar
              </Button>
              <Button color="success" size="sm" style={{ minWidth: 100 }} onClick={guardar} disabled={guardando}>
                {guardando ? "Guardando…" : "Aplicar"}
              </Button>
            </span>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};

export default ClasificarProveedorModal;
